<template>
    <!-- The one control every report uses to say what window it covers.
         Presets handle the common cases; the two pickers handle everything
         else. The resulting window is always stated in plain English, so the
         user never has to infer what will actually be reported on. -->
    <div>
        <!-- Quick-select presets. On a phone they lay out as a full-width
             3-column grid: every chip is one column, so all chips are a
             consistent width, the first row fills three across, and the last row
             (two chips) aligns under the first with its third cell empty. Each
             button is also a full 48px (~8mm) tall tap target -- the accessible
             minimum matching the rest of the report controls. From sm up it drops
             back to the natural inline flex-wrap row where a mouse points. -->
        <div class="grid grid-cols-3 gap-2 sm:flex sm:flex-wrap">
            <button v-for="preset in availablePresets"
                    :key="preset.key"
                    type="button"
                    class="inline-flex w-full min-h-[48px] items-center justify-center rounded-md border px-3 py-1.5 text-sm font-medium transition-colors duration-150 sm:w-auto sm:min-h-0"
                    :class="preset.key === activePresetKey
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'"
                    @click="applyPreset(preset)"
            >
                {{ preset.label }}
            </button>

            <!-- Custom has no fixed range of its own: it is the state the control
                 is in whenever the dates match none of the presets above, i.e. the
                 user has typed their own range in the pickers below. It lights
                 automatically in that case, and also as soon as it is clicked, so
                 selecting it gives the same immediate feedback as a preset.
                 Clicking it also focuses the From picker to show where to type. -->
            <button type="button"
                    class="inline-flex w-full min-h-[48px] items-center justify-center rounded-md border px-3 py-1.5 text-sm font-medium transition-colors duration-150 sm:w-auto sm:min-h-0"
                    :class="customActive
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'"
                    @click="chooseCustom"
            >
                Custom
            </button>
        </div>

        <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
                <label  :for="`${idPrefix}_start`"
                        class="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
                    From
                </label>
                <date-time-picker   :id="`${idPrefix}_start`"
                                    :model-value="startDate"
                                    :min-date="minDate"
                                    :max-date="maxDate"
                                    :timezone="timezone"
                                    format="YYYY-MM-DD"
                                    view-mode="days"
                                    @update:modelValue="onStartChange"
                ></date-time-picker>
            </div>

            <div>
                <label  :for="`${idPrefix}_end`"
                        class="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
                    To
                </label>
                <date-time-picker   :id="`${idPrefix}_end`"
                                    :model-value="endDate"
                                    :min-date="minDate"
                                    :max-date="maxDate"
                                    :timezone="timezone"
                                    format="YYYY-MM-DD"
                                    view-mode="days"
                                    @update:modelValue="onEndChange"
                ></date-time-picker>
            </div>
        </div>

        <p  v-if="summary"
            class="m-0 mt-2 text-sm"
            :class="rangeError ? 'text-red-600' : 'text-gray-500'"
        >
            {{ rangeError || summary }}
        </p>
    </div>
</template>

<script>
import {DateTime} from "luxon"

import DateTimePicker from "@/components/Reports/VueDatePicker.vue"

/**
 * Serialize a range boundary for the report POST body.
 *
 * The wire format is a calendar date ("2026-07-16"), NOT a Date object.
 *
 * This exists because handing a raw Date to axios serialises it to a UTC
 * instant ("2026-07-16T04:00:00.000Z"), which is the same calendar day only for
 * companies at or ahead of UTC. Behind UTC, the receiving controller has to undo
 * that conversion to recover the day the user actually picked, and getting it
 * wrong shifts the whole report window by a day.
 *
 * Sending the plain date removes the ambiguity: there is no offset to
 * misinterpret, and the backend anchors it in the company timezone.
 *
 * The calendar day is read in the company timezone, not the browser's. A Date is
 * an instant; the same instant is a different calendar day depending on the zone
 * you read it in. Reading it in the company zone means the date posted is the day
 * the user saw in the picker (which is also anchored on the company zone), so a
 * user in a different timezone than the company does not shift the window by a
 * day.
 *
 * Use this for anything posting a ReportDateRange value.
 */
export const toReportDate = (value, timezone = 'America/New_York') => {
    if (! value) {
        return null
    }

    const date = value instanceof Date ? value : new Date(value)

    return isNaN(date.getTime())
        ? null
        : DateTime.fromJSDate(date, {zone: timezone}).toFormat('yyyy-MM-dd')
}

export default {
    name: "ReportDateRange",
    components: {DateTimePicker},
    emits: ['update:start', 'update:end', 'update:valid'],
    props: {
        start: {
            type: [Date, String],
            default: null,
        },
        end: {
            type: [Date, String],
            default: null,
        },
        // Earliest selectable date, normally the company's creation date.
        minDate: {
            type: [Date, String],
            default: null,
        },
        // Latest selectable date, normally today.
        maxDate: {
            type: [Date, String],
            default: null,
        },
        // Guard rail: a long range over many points builds slowly and produces
        // an unwieldy PDF. 31 days is the cap, which still allows any single
        // calendar month (spanDays counts inclusively, so July 1-31 is 31).
        maxSpanDays: {
            type: Number,
            default: 31,
        },
        timezone: {
            type: String,
            default: 'America/New_York',
        },
        idPrefix: {
            type: String,
            default: 'report_range',
        },
    },
    data() {
        return {
            // Set when the user clicks Custom. Without it the chip could only
            // light once the dates already differed from every preset, so the
            // click that announces the intent appeared to do nothing.
            _customChosen: false,
        }
    },
    computed: {
        startDate() {
            return this.toDate(this.start)
        },
        endDate() {
            return this.toDate(this.end)
        },
        spanDays() {
            if (! this.startDate || ! this.endDate) {
                return null
            }

            return Math.round(
                this.zoned(this.endDate).endOf('day')
                    .diff(this.zoned(this.startDate).startOf('day'), 'days').days
            )
        },
        rangeError() {
            if (! this.startDate || ! this.endDate) {
                return null
            }

            if (this.endDate < this.startDate) {
                return 'The end date must be on or after the start date.'
            }

            if (this.spanDays > this.maxSpanDays) {
                return `Reports cover at most ${this.maxSpanDays} days. Shorten the range, or run a separate report for each month.`
            }

            return null
        },
        summary() {
            if (! this.startDate || ! this.endDate) {
                return null
            }

            const from = this.zoned(this.startDate)
            const to = this.zoned(this.endDate)
            const days = this.spanDays

            if (from.hasSame(to, 'day')) {
                return `Covers ${from.toFormat('LLLL d, yyyy')} (1 day)`
            }

            const sameYear = from.hasSame(to, 'year')
            const fromLabel = sameYear ? from.toFormat('LLLL d') : from.toFormat('LLLL d, yyyy')

            return `Covers ${fromLabel} - ${to.toFormat('LLLL d, yyyy')} (${days} days)`
        },
        // Hide presets the company has no history for, and any that would
        // exceed the span cap: offering a choice that immediately errors is
        // worse than not offering it.
        availablePresets() {
            return this.presets.filter((preset) => {
                const {start, end} = preset.range()

                if (this.minLimit && start < this.minLimit) {
                    return false
                }

                const span = Math.round(
                    this.zoned(end).endOf('day')
                        .diff(this.zoned(start).startOf('day'), 'days').days
                )

                return span <= this.maxSpanDays
            })
        },
        minLimit() {
            return this.toDate(this.minDate)
        },
        maxLimit() {
            return this.toDate(this.maxDate) ?? new Date()
        },
        presets() {
            // "Today" and the month boundaries are the company's, not the
            // browser's: a preset like "This month" must mean the company's
            // current month even when the user's clock is in another zone.
            const today = this.zoned(this.maxLimit)

            return [
                {
                    key: 'this-month',
                    label: 'This month',
                    range: () => ({start: today.startOf('month').toJSDate(), end: today.toJSDate()}),
                },
                {
                    key: 'last-month',
                    label: 'Last month',
                    range: () => {
                        const previous = today.minus({months: 1})
                        return {start: previous.startOf('month').toJSDate(), end: previous.endOf('month').toJSDate()}
                    },
                },
                {
                    key: 'last-7',
                    label: 'Last 7 days',
                    range: () => ({start: today.minus({days: 6}).startOf('day').toJSDate(), end: today.toJSDate()}),
                },
                {
                    key: 'last-30',
                    label: 'Last 30 days',
                    range: () => ({start: today.minus({days: 29}).startOf('day').toJSDate(), end: today.toJSDate()}),
                },
                {
                    key: 'last-90',
                    label: 'Last 90 days',
                    range: () => ({start: today.minus({days: 89}).startOf('day').toJSDate(), end: today.toJSDate()}),
                },
            ]
        },
        // Highlight whichever preset matches the current range, so a preset
        // stays lit until the dates are edited away from it.
        activePresetKey() {
            // An explicit Custom click wins over an incidental preset match, so
            // the two chips can never be lit at once.
            if (this.$data._customChosen || ! this.startDate || ! this.endDate) {
                return null
            }

            const match = this.presets.find((preset) => {
                const {start, end} = preset.range()
                return this.zoned(start).hasSame(this.zoned(this.startDate), 'day')
                    && this.zoned(end).hasSame(this.zoned(this.endDate), 'day')
            })

            return match ? match.key : null
        },
        // Custom is "on" once the user clicks it, and also whenever a full range
        // is set that matches none of the presets -- that is exactly a
        // user-typed range. With no range and no click it is off, so a blank
        // form does not read as already customised.
        customActive() {
            return this.$data._customChosen
                || !! (this.startDate && this.endDate && this.activePresetKey === null)
        },
    },
    watch: {
        rangeError: {
            handler(value) {
                this.$emit('update:valid', ! value)
            },
            immediate: true,
        },
    },
    methods: {
        // Read a JS Date as a Luxon DateTime anchored in the company timezone,
        // so every day/month calculation in this control (spans, presets, "this
        // month", the summary) reflects the company's calendar rather than the
        // browser's. Without this a user in a different zone than the company can
        // see the window off by a day.
        zoned(date) {
            return DateTime.fromJSDate(date, {zone: this.timezone})
        },
        toDate(value) {
            if (value instanceof Date) {
                return isNaN(value.getTime()) ? null : value
            }

            if (typeof value === 'string' && value !== '') {
                const parsed = new Date(value)
                return isNaN(parsed.getTime()) ? null : parsed
            }

            return null
        },
        applyPreset(preset) {
            this.$data._customChosen = false

            const {start, end} = preset.range()
            this.$emit('update:start', start)
            this.$emit('update:end', end)
        },
        // Clicking "Custom" selects it and points the user at where a custom
        // range is entered. vue-datepicker ids its input `dp-input-<uid>`, and
        // our uid is `<idPrefix>_start`; focusing it opens the picker so they
        // can pick or type a date straight away.
        chooseCustom() {
            this.$data._customChosen = true

            const input = document.getElementById(`dp-input-${this.idPrefix}_start`)

            if (input) {
                input.focus()
            }
        },
        onStartChange(value) {
            const next = this.toDate(value)
            this.$emit('update:start', next)

            // Keep the range valid rather than leaving the user in an error
            // state they then have to fix by hand.
            if (next && this.endDate && this.endDate < next) {
                this.$emit('update:end', next)
            }
        },
        onEndChange(value) {
            const next = this.toDate(value)
            this.$emit('update:end', next)

            if (next && this.startDate && next < this.startDate) {
                this.$emit('update:start', next)
            }
        },
    },
}
</script>

<style scoped>
/*
 * From / To date inputs: on a phone give the vue-datepicker input a 48px (~8mm)
 * minimum height and a 16px font -- the accessible tap-target minimum matching
 * the preset chips and the other report controls, and enough font to stop iOS
 * Safari auto-zooming on focus. :deep reaches the input inside the child
 * component; the natural desktop size returns from sm up.
 */
@media (max-width: 639px) {
    :deep(.dp__input) {
        min-height: 48px;
        font-size: 16px;
    }
}
</style>
